import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Button,
    Badge,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Select,
    Wrap,
    WrapItem,
    Input,
    Tooltip,
    useBreakpointValue,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Icon,
} from '@chakra-ui/react';
import { RepeatIcon, InfoIcon, CloseIcon } from '@chakra-ui/icons';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    Line,
    ComposedChart,
    Area,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
} from 'recharts';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

// Bias code mappings
const BIAS_CODES = {
    OVERCONF: 'Overconfidence',
    LOSSAV: 'Loss Aversion',
    ANCHOR: 'Anchoring',
    HERD: 'Herd Behavior',
    FAMILIAR: 'Familiarity Bias',
    CONFIRM: 'Confirmation Bias',
    RECENCY: 'Recency Bias',
    AFFECT: 'Affect Infusion',
    MENTAL: 'Mental Accounting',
    IMPACT: 'Impact Bias',
    DISPOS: 'Disposition Effect',
    COGNITIVE: 'Cognitive Dissonance',
    TAXAV: 'Tax Aversion',
    REGRET: 'Regret Aversion',
    EMOTION: 'Emotional State',
};

function DashboardHeadBiasBenchmarkPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [loading, setLoading] = useState(false);
    const [benchmarkData, setBenchmarkData] = useState(null);
    const toast = useToast();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.300';
    const chartGridColor = isDarkTheme ? 'rgba(66, 153, 225, 0.25)' : '#E2E8F0';

    // Status colors - lighter for dark theme
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';
    const warningColor = isDarkTheme ? '#F6AD55' : '#DD6B20';

    // Info card backgrounds - improved transparency for dark theme
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '15';
    const successBg = isDarkTheme ? 'rgba(72, 187, 120, 0.15)' : '#F0FFF4';
    const errorBg = isDarkTheme ? 'rgba(252, 129, 129, 0.15)' : '#FFF5F5';

    // Chart colors - brighter for dark theme
    const chartColor1 = isDarkTheme ? accentColor : primaryColor;
    const chartColor2 = successColor;
    const chartColor3 = isDarkTheme ? '#B794F4' : '#805AD5';
    const chartColor4 = warningColor;

    // Placeholder/empty background
    const placeholderBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.50';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Filter states
    const [tenantScope, setTenantScope] = useState('firm');
    const [timeWindow, setTimeWindow] = useState('last_90d');
    const [metrics, setMetrics] = useState('mean,p50,p75');
    const [selectedBiases, setSelectedBiases] = useState([]);
    const [segment, setSegment] = useState('');
    const [country, setCountry] = useState('');
    const [wealthBand, setWealthBand] = useState('');
    const [riskBucket, setRiskBucket] = useState('');

    // Responsive
    const isMobile = useBreakpointValue({ base: true, md: false });
    const chartHeight = useBreakpointValue({ base: 350, md: 450 });

    // Fetch benchmark data
    const fetchBenchmarkData = async () => {
        try {
            setLoading(true);
            const params = {
                tenant_scope: tenantScope,
                time_window: timeWindow,
                metrics: metrics,
            };

            if (selectedBiases.length > 0) {
                params.bias = selectedBiases.join(',');
            }
            if (segment) params.segment = segment;
            if (country) params.country = country;
            if (wealthBand) params.wealth_band = wealthBand;
            if (riskBucket) params.risk_bucket = riskBucket;

            const res = await axios.get(`${API_BASE_URL}/content/admin-bias-benchmark`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setBenchmarkData(res.data);

            if (!res.data.series || res.data.series.length === 0) {
                toast({
                    title: 'No Data',
                    description: 'No benchmark data available for the selected filters.',
                    status: 'info',
                    duration: 4000,
                    isClosable: true,
                    position: 'top',
                });
            }
        } catch (error) {
            console.error('Benchmark fetch error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load benchmark data.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
            setBenchmarkData(null);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchBenchmarkData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Toggle bias selection
    const toggleBias = (biasCode) => {
        setSelectedBiases((prev) =>
            prev.includes(biasCode)
                ? prev.filter((b) => b !== biasCode)
                : [...prev, biasCode]
        );
    };

    // Clear all filters
    const clearFilters = () => {
        setTenantScope('firm');
        setTimeWindow('last_90d');
        setMetrics('mean,p50,p75');
        setSelectedBiases([]);
        setSegment('');
        setCountry('');
        setWealthBand('');
        setRiskBucket('');
    };

    // Get color for score with gradient (like DashboardAdminScoresPaid)
    const getScoreColor = (score) => {
        if (score >= 80) return '#8B0000'; // Dark red
        if (score >= 70) return '#DC143C'; // Crimson
        if (score >= 60) return '#FF6347'; // Tomato
        if (score >= 50) return '#FF8C00'; // Dark orange
        if (score >= 40) return '#FFA500'; // Orange
        if (score >= 30) return '#FFD700'; // Gold
        if (score >= 20) return '#FFFFE0'; // Light yellow
        if (score >= 10) return '#90EE90'; // Light green
        return '#228B22'; // Forest green
    };

    // Prepare chart data from series
    const prepareChartData = () => {
        if (!benchmarkData || !benchmarkData.series || benchmarkData.series.length === 0) {
            return [];
        }

        return benchmarkData.series.map((item) => {
            const chartItem = {
                bias: BIAS_CODES[item.bias] || item.bias,
                biasCode: item.bias,
                n: item.n,
            };

            // Add all available metrics
            if (item.mean !== undefined) chartItem.mean = parseFloat(item.mean.toFixed(2));
            if (item.median !== undefined) chartItem.median = parseFloat(item.median.toFixed(2));
            if (item.p50 !== undefined) chartItem.p50 = parseFloat(item.p50.toFixed(2));
            if (item.p25 !== undefined) chartItem.p25 = parseFloat(item.p25.toFixed(2));
            if (item.p75 !== undefined) chartItem.p75 = parseFloat(item.p75.toFixed(2));

            return chartItem;
        });
    };

    // Render seaborn-style heatmap
    const renderHeatmap = () => {
        const data = prepareChartData();
        if (data.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No data available.</Text>
                </Flex>
            );
        }

        const cols = 5;
        const rows = Math.ceil(data.length / cols);

        return (
            <Box 
                bg={isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : cardBg}
                p={4} 
                borderRadius="md" 
                border="1px solid"
                borderColor={borderColor}
                overflow="auto"
            >
                {/* Color scale legend */}
                <Flex justify="flex-end" mb={4} align="center">
                    <Text fontSize="xs" fontWeight="bold" mr={2} color={bodyTextColor}>
                        Score:
                    </Text>
                    <HStack spacing={0}>
                        {[0, 20, 40, 60, 80, 100].map((val) => (
                            <Box key={val} textAlign="center">
                                <Box
                                    w="30px"
                                    h="15px"
                                    bg={getScoreColor(val)}
                                    border="1px solid"
                                    borderColor={borderColor}
                                />
                                <Text fontSize="9px" color={bodyTextColor}>{val}</Text>
                            </Box>
                        ))}
                    </HStack>
                </Flex>

                {/* Heatmap grid */}
                <Box 
                    display="inline-block"
                    border="1px solid"
                    borderColor={borderColor}
                >
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <Flex key={rowIndex}>
                            {Array.from({ length: cols }).map((_, colIndex) => {
                                const index = rowIndex * cols + colIndex;
                                if (index >= data.length) {
                                    return (
                                        <Box
                                            key={colIndex}
                                            w="140px"
                                            h="80px"
                                            bg={placeholderBg}
                                            borderRight="1px solid"
                                            borderBottom="1px solid"
                                            borderColor={borderColor}
                                        />
                                    );
                                }

                                const item = data[index];
                                const scoreValue = item.mean || item.p50 || 0;
                                
                                return (
                                    <Tooltip
                                        key={colIndex}
                                        label={
                                            <Box>
                                                <Text fontWeight="bold">{item.bias}</Text>
                                                <Text>Mean: {item.mean?.toFixed(1) || 'N/A'}</Text>
                                                <Text>Median: {item.p50?.toFixed(1) || 'N/A'}</Text>
                                                <Text>P75: {item.p75?.toFixed(1) || 'N/A'}</Text>
                                                <Text>Sample: {item.n}</Text>
                                            </Box>
                                        }
                                        placement="top"
                                        hasArrow
                                        bg={isDarkTheme ? 'gray.800' : 'gray.700'}
                                        color="white"
                                    >
                                        <Box
                                            w="140px"
                                            h="80px"
                                            bg={getScoreColor(scoreValue)}
                                            borderRight="1px solid"
                                            borderBottom="1px solid"
                                            borderColor={borderColor}
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            cursor="pointer"
                                            transition="all 0.2s"
                                            position="relative"
                                            _hover={{ 
                                                transform: 'scale(1.05)',
                                                zIndex: 10,
                                                boxShadow: '0 0 10px rgba(0,0,0,0.3)',
                                            }}
                                        >
                                            <Text
                                                fontSize="xs"
                                                fontWeight="bold"
                                                color={scoreValue > 50 ? "white" : "gray.800"}
                                                textAlign="center"
                                                px={2}
                                                noOfLines={2}
                                                lineHeight="1.2"
                                            >
                                                {item.bias}
                                            </Text>
                                            <Text
                                                fontSize="2xl"
                                                fontWeight="bold"
                                                color={scoreValue > 50 ? "white" : "gray.800"}
                                                mt={1}
                                            >
                                                {scoreValue.toFixed(0)}
                                            </Text>
                                            <Badge
                                                colorScheme="gray"
                                                fontSize="8px"
                                                position="absolute"
                                                top={1}
                                                right={1}
                                            >
                                                n={item.n}
                                            </Badge>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Flex>
                    ))}
                </Box>
            </Box>
        );
    };

    // Render comparison bar chart
    const renderComparisonChart = () => {
        const data = prepareChartData();
        if (data.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No data available for selected filters.</Text>
                </Flex>
            );
        }

        const metricsToShow = metrics.split(',').map(m => m.trim());

        return (
            <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: isMobile ? 100 : 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis
                        dataKey="bias"
                        angle={-45}
                        textAnchor="end"
                        height={isMobile ? 120 : 100}
                        stroke={isDarkTheme ? bodyTextColor : '#4A5568'}
                        interval={0}
                        tick={{ fontSize: isMobile ? 9 : 10, fill: isDarkTheme ? bodyTextColor : '#4A5568' }}
                    />
                    <YAxis 
                        domain={[0, 100]} 
                        stroke={isDarkTheme ? bodyTextColor : '#4A5568'} 
                        tick={{ fill: isDarkTheme ? bodyTextColor : '#4A5568' }} 
                    />
                    <RechartsTooltip
                        contentStyle={{
                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                            border: `1px solid ${borderColor}`,
                            borderRadius: '8px',
                            padding: '8px',
                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                        }}
                        formatter={(value, name) => [value, name.toUpperCase()]}
                    />
                    <Legend 
                        wrapperStyle={{ 
                            paddingTop: '20px',
                            color: bodyTextColor 
                        }} 
                    />
                    {metricsToShow.includes('mean') && (
                        <Bar dataKey="mean" fill={chartColor1} name="Mean" radius={[8, 8, 0, 0]} />
                    )}
                    {(metricsToShow.includes('p50') || metricsToShow.includes('median')) && (
                        <Bar dataKey={metricsToShow.includes('p50') ? 'p50' : 'median'} fill={chartColor2} name="P50 (Median)" radius={[8, 8, 0, 0]} />
                    )}
                    {metricsToShow.includes('p25') && (
                        <Bar dataKey="p25" fill={chartColor3} name="P25" radius={[8, 8, 0, 0]} />
                    )}
                    {metricsToShow.includes('p75') && (
                        <Bar dataKey="p75" fill={chartColor4} name="P75" radius={[8, 8, 0, 0]} />
                    )}
                </BarChart>
            </ResponsiveContainer>
        );
    };

    // Render distribution chart (P25, P50, P75)
    const renderDistributionChart = () => {
        const data = prepareChartData();
        if (data.length === 0 || !data[0]?.p25) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>Distribution data not available. Select metrics with P25 and P75.</Text>
                </Flex>
            );
        }

        return (
            <ResponsiveContainer width="100%" height={chartHeight}>
                <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: isMobile ? 100 : 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis
                        dataKey="bias"
                        angle={-45}
                        textAnchor="end"
                        height={isMobile ? 120 : 100}
                        stroke={isDarkTheme ? bodyTextColor : '#4A5568'}
                        interval={0}
                        tick={{ fontSize: isMobile ? 9 : 10, fill: isDarkTheme ? bodyTextColor : '#4A5568' }}
                    />
                    <YAxis 
                        domain={[0, 100]} 
                        stroke={isDarkTheme ? bodyTextColor : '#4A5568'} 
                        tick={{ fill: isDarkTheme ? bodyTextColor : '#4A5568' }} 
                    />
                    <RechartsTooltip
                        contentStyle={{
                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                            border: `1px solid ${borderColor}`,
                            borderRadius: '8px',
                            padding: '8px',
                            color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                        }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                    <Area
                        type="monotone"
                        dataKey="p75"
                        fill={isDarkTheme ? 'rgba(99, 179, 237, 0.3)' : accentColor + '30'}
                        stroke={chartColor1}
                        fillOpacity={0.3}
                        name="P75"
                    />
                    <Area
                        type="monotone"
                        dataKey="p25"
                        fill={isDarkTheme ? 'rgba(45, 55, 72, 0.5)' : cardBg}
                        stroke={chartColor3}
                        fillOpacity={0.5}
                        name="P25"
                    />
                    <Line
                        type="monotone"
                        dataKey="p50"
                        stroke={chartColor2}
                        strokeWidth={3}
                        dot={{ fill: chartColor2, r: 5 }}
                        name="Median (P50)"
                    />
                </ComposedChart>
            </ResponsiveContainer>
        );
    };

    // Render radar chart for top biases
    const renderRadarChart = () => {
        const data = prepareChartData();
        if (data.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No data available.</Text>
                </Flex>
            );
        }

        const topBiases = data
            .sort((a, b) => (b.mean || b.p50 || 0) - (a.mean || a.p50 || 0))
            .slice(0, 8)
            .map((item) => ({
                bias: item.bias,
                score: item.mean || item.p50 || 0,
            }));

        return (
            <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={topBiases}>
                    <PolarGrid stroke={chartGridColor} />
                    <PolarAngleAxis 
                        dataKey="bias" 
                        stroke={isDarkTheme ? bodyTextColor : '#4A5568'} 
                        tick={{ fontSize: 8, fill: isDarkTheme ? bodyTextColor : '#4A5568' }} 
                    />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar name="Bias Score" dataKey="score" stroke={chartColor1} fill={chartColor1} fillOpacity={0.6} />
                    <RechartsTooltip 
                        contentStyle={{ 
                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg, 
                            border: `1px solid ${borderColor}`, 
                            borderRadius: '8px', 
                            color: isDarkTheme ? '#E2E8F0' : '#1A202C' 
                        }} 
                    />
                </RadarChart>
            </ResponsiveContainer>
        );
    };

    // Calculate summary statistics
    const calculateSummaryStats = () => {
        if (!benchmarkData || !benchmarkData.series || benchmarkData.series.length === 0) {
            return null;
        }

        const series = benchmarkData.series;
        const totalSamples = series.reduce((sum, item) => sum + (item.n || 0), 0);
        const avgMean = series.reduce((sum, item) => sum + (item.mean || 0), 0) / series.length;
        const highestBias = series.reduce((max, item) =>
            (item.mean || 0) > (max.mean || 0) ? item : max
        );
        const lowestBias = series.reduce((min, item) =>
            (item.mean || 0) < (min.mean || 0) ? item : min
        );

        return {
            totalSamples,
            avgMean,
            highestBias,
            lowestBias,
            totalBiases: series.length,
        };
    };

    const stats = calculateSummaryStats();
    const hasActiveFilters = segment || country || wealthBand || riskBucket || selectedBiases.length > 0;

    return (
        <Container maxW="container.xl" py={6}>
            <VStack spacing={6} align="stretch">
                {/* Header */}
                <Box>
                    <Heading size={isMobile ? 'md' : 'lg'} color={headingTextColor} mb={2}>
                        Department Head - Bias Benchmark Analysis
                    </Heading>
                    <Text color={bodyTextColor} fontSize="sm">
                        Department view: Review and analyze bias benchmarks for your team's customers
                    </Text>
                </Box>

                {/* Filters Card */}
                <Card 
                    bg={cardBg} 
                    borderRadius="lg" 
                    boxShadow="md" 
                    borderWidth="1px" 
                    borderColor={borderColor}
                >
                    <CardBody>
                        <VStack spacing={4} align="stretch">
                            <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                                <Heading size="sm" color={headingTextColor}>
                                    Filters & Configuration
                                </Heading>
                                <HStack spacing={2}>
                                    {hasActiveFilters && (
                                        <Button
                                            size="sm"
                                            leftIcon={<CloseIcon boxSize={3} />}
                                            colorScheme="red"
                                            variant="ghost"
                                            onClick={clearFilters}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        leftIcon={<RepeatIcon />}
                                        variant="ghost"
                                        color={isDarkTheme ? accentColor : primaryColor}
                                        onClick={fetchBenchmarkData}
                                        isLoading={loading}
                                    >
                                        Refresh
                                    </Button>
                                </HStack>
                            </Flex>

                            {/* Main Filters */}
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                                {/* Tenant Scope */}
                                <Box>
                                    <Text fontSize="sm" fontWeight="medium" mb={2} color={labelTextColor}>
                                        Scope
                                    </Text>
                                    <Select
                                        value={tenantScope}
                                        onChange={(e) => setTenantScope(e.target.value)}
                                        borderColor={borderColor}
                                        size={isMobile ? 'sm' : 'md'}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    >
                                        <option value="firm">Firm</option>
                                        <option value="global">Global</option>
                                    </Select>
                                </Box>

                                {/* Time Window */}
                                <Box>
                                    <Text fontSize="sm" fontWeight="medium" mb={2} color={labelTextColor}>
                                        Time Window
                                    </Text>
                                    <Select
                                        value={timeWindow}
                                        onChange={(e) => setTimeWindow(e.target.value)}
                                        borderColor={borderColor}
                                        size={isMobile ? 'sm' : 'md'}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    >
                                        <option value="last_30d">Last 30 Days</option>
                                        <option value="last_90d">Last 90 Days</option>
                                        <option value="ytd">Year to Date</option>
                                        <option value="inception">All Time</option>
                                    </Select>
                                </Box>

                                {/* Metrics */}
                                <Box>
                                    <Text fontSize="sm" fontWeight="medium" mb={2} color={labelTextColor}>
                                        Metrics
                                    </Text>
                                    <Select
                                        value={metrics}
                                        onChange={(e) => setMetrics(e.target.value)}
                                        borderColor={borderColor}
                                        size={isMobile ? 'sm' : 'md'}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    >
                                        <option value="mean">Mean</option>
                                        <option value="mean,p50">Mean + Median</option>
                                        <option value="mean,p50,p75">Mean + P50 + P75</option>
                                        <option value="mean,p25,p50,p75">All Percentiles</option>
                                    </Select>
                                </Box>

                                {/* Segment */}
                                <Box>
                                    <Text fontSize="sm" fontWeight="medium" mb={2} color={labelTextColor}>
                                        Segment
                                    </Text>
                                    <Select
                                        value={segment}
                                        onChange={(e) => setSegment(e.target.value)}
                                        borderColor={borderColor}
                                        placeholder="All Segments"
                                        size={isMobile ? 'sm' : 'md'}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    >
                                        <option value="risk_averse">Risk Averse</option>
                                        <option value="overconfident">Overconfident</option>
                                        <option value="herd_followers">Herd Followers</option>
                                        <option value="anchored">Anchored</option>
                                        <option value="balanced">Balanced</option>
                                    </Select>
                                </Box>
                            </SimpleGrid>

                            {/* Advanced Filters (Collapsible) */}
                            <Accordion allowToggle>
                                <AccordionItem border="none">
                                    <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                                        <Box flex="1" textAlign="left">
                                            <Text fontSize="sm" fontWeight="medium" color={isDarkTheme ? accentColor : primaryColor}>
                                                Advanced Filters
                                            </Text>
                                        </Box>
                                        <AccordionIcon color={isDarkTheme ? accentColor : primaryColor} />
                                    </AccordionButton>
                                    <AccordionPanel px={0} pt={4}>
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                            {/* Country */}
                                            <Box>
                                                <Text fontSize="sm" fontWeight="medium" mb={2} color={labelTextColor}>
                                                    Country Code
                                                </Text>
                                                <Input
                                                    value={country}
                                                    onChange={(e) => setCountry(e.target.value.toUpperCase())}
                                                    placeholder="e.g., US, UK, IN"
                                                    borderColor={borderColor}
                                                    size={isMobile ? 'sm' : 'md'}
                                                    maxLength={3}
                                                    _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                                    bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                                    color={bodyTextColor}
                                                />
                                            </Box>

                                            {/* Wealth Band */}
                                            <Box>
                                                <Text fontSize="sm" fontWeight="medium" mb={2} color={labelTextColor}>
                                                    Wealth Band
                                                </Text>
                                                <Input
                                                    value={wealthBand}
                                                    onChange={(e) => setWealthBand(e.target.value)}
                                                    placeholder="e.g., high, medium, low"
                                                    borderColor={borderColor}
                                                    size={isMobile ? 'sm' : 'md'}
                                                    _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                                    bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                                    color={bodyTextColor}
                                                />
                                            </Box>

                                            {/* Risk Bucket */}
                                            <Box>
                                                <Text fontSize="sm" fontWeight="medium" mb={2} color={labelTextColor}>
                                                    Risk Bucket
                                                </Text>
                                                <Input
                                                    value={riskBucket}
                                                    onChange={(e) => setRiskBucket(e.target.value)}
                                                    placeholder="e.g., 5-7"
                                                    borderColor={borderColor}
                                                    size={isMobile ? 'sm' : 'md'}
                                                    _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                                    bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                                    color={bodyTextColor}
                                                />
                                            </Box>
                                        </SimpleGrid>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>

                            {/* Bias Selection */}
                            <Box>
                                <Flex align="center" mb={2}>
                                    <Text fontSize="sm" fontWeight="medium" color={labelTextColor} mr={2}>
                                        Select Specific Biases
                                    </Text>
                                    <Tooltip label="Click to filter specific biases. Leave empty to show all biases." placement="top">
                                        <Icon as={InfoIcon} color={mutedTextColor} boxSize={3} />
                                    </Tooltip>
                                    {selectedBiases.length > 0 && (
                                        <Badge ml={2} colorScheme="blue" fontSize="xs">
                                            {selectedBiases.length} selected
                                        </Badge>
                                    )}
                                </Flex>
                                <Wrap spacing={2}>
                                    {Object.entries(BIAS_CODES).map(([code, name]) => (
                                        <WrapItem key={code}>
                                            <Badge
                                                px={3}
                                                py={1}
                                                borderRadius="full"
                                                cursor="pointer"
                                                bg={selectedBiases.includes(code) ? primaryColor : placeholderBg}
                                                color={selectedBiases.includes(code) ? 'white' : bodyTextColor}
                                                _hover={{
                                                    bg: selectedBiases.includes(code) ? buttonHoverBg : borderColor,
                                                }}
                                                onClick={() => toggleBias(code)}
                                                fontSize="xs"
                                                transition="all 0.2s"
                                                boxShadow={selectedBiases.includes(code) ? 'sm' : 'none'}
                                            >
                                                {name}
                                            </Badge>
                                        </WrapItem>
                                    ))}
                                </Wrap>
                            </Box>

                            <Button
                                bg={primaryColor}
                                color="white"
                                onClick={fetchBenchmarkData}
                                isLoading={loading}
                                loadingText="Loading..."
                                size={isMobile ? 'sm' : 'md'}
                                _hover={{ bg: buttonHoverBg }}
                                boxShadow="md"
                            >
                                Apply Filters & Refresh
                            </Button>
                        </VStack>
                    </CardBody>
                </Card>

                {/* Loading State */}
                {loading && (
                    <Flex justify="center" align="center" py={20}>
                        <VStack spacing={4}>
                            <Spinner size="xl" color={primaryColor} thickness="4px" />
                            <Text color={bodyTextColor}>Loading benchmark data...</Text>
                        </VStack>
                    </Flex>
                )}

                {/* Results */}
                {!loading && benchmarkData && benchmarkData.series && benchmarkData.series.length > 0 && (
                    <>
                        {/* Summary Stats */}
                        {stats && (
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                                <Card 
                                    bg={infoBg} 
                                    borderRadius="lg" 
                                    boxShadow="md" 
                                    transition="all 0.2s" 
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} 
                                    borderWidth="1px" 
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color={bodyTextColor} fontSize="sm">Total Biases</StatLabel>
                                            <StatNumber color={isDarkTheme ? accentColor : primaryColor} fontSize={isMobile ? 'xl' : '2xl'}>{stats.totalBiases}</StatNumber>
                                            <StatHelpText color={mutedTextColor}>Analyzed</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                <Card 
                                    bg={infoBg} 
                                    borderRadius="lg" 
                                    boxShadow="md" 
                                    transition="all 0.2s" 
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} 
                                    borderWidth="1px" 
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color={bodyTextColor} fontSize="sm">Average Score</StatLabel>
                                            <StatNumber color={isDarkTheme ? accentColor : primaryColor} fontSize={isMobile ? 'xl' : '2xl'}>{stats.avgMean.toFixed(1)}</StatNumber>
                                            <StatHelpText color={mutedTextColor}>Across All Biases</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                <Card 
                                    bg={errorBg} 
                                    borderRadius="lg" 
                                    boxShadow="md" 
                                    transition="all 0.2s" 
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} 
                                    borderWidth="1px" 
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color={bodyTextColor} fontSize="sm">Highest Bias</StatLabel>
                                            <StatNumber color={errorColor} fontSize={isMobile ? 'md' : 'lg'} noOfLines={1}>
                                                {BIAS_CODES[stats.highestBias.bias]}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor}>{stats.highestBias.mean?.toFixed(1)}</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                                <Card 
                                    bg={successBg} 
                                    borderRadius="lg" 
                                    boxShadow="md" 
                                    transition="all 0.2s" 
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} 
                                    borderWidth="1px" 
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color={bodyTextColor} fontSize="sm">Lowest Bias</StatLabel>
                                            <StatNumber color={successColor} fontSize={isMobile ? 'md' : 'lg'} noOfLines={1}>
                                                {BIAS_CODES[stats.lowestBias.bias]}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor}>{stats.lowestBias.mean?.toFixed(1)}</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            </SimpleGrid>
                        )}

                        {/* Heatmap */}
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Heading size={isMobile ? 'sm' : 'md'} mb={2} color={headingTextColor}>
                                    Bias Score Heatmap
                                </Heading>
                                <Text fontSize="sm" color={bodyTextColor} mb={4}>
                                    Color intensity indicates bias severity. Hover over cells for detailed metrics.
                                </Text>
                                {renderHeatmap()}
                            </CardBody>
                        </Card>

                        {/* Radar Chart and Comparison Chart Side by Side */}
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <Heading size={isMobile ? 'sm' : 'md'} mb={4} color={headingTextColor}>
                                        Top 8 Biases (Radar View)
                                    </Heading>
                                    {renderRadarChart()}
                                </CardBody>
                            </Card>
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <Heading size={isMobile ? 'sm' : 'md'} mb={4} color={headingTextColor}>
                                        Metric Comparison
                                    </Heading>
                                    {renderComparisonChart()}
                                </CardBody>
                            </Card>
                        </SimpleGrid>

                        {/* Distribution Chart */}
                        {metrics.includes('p25') && metrics.includes('p75') && (
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <Heading size={isMobile ? 'sm' : 'md'} mb={2} color={headingTextColor}>
                                        Score Distribution (P25 - P50 - P75)
                                    </Heading>
                                    <Text fontSize="sm" color={bodyTextColor} mb={4}>
                                        Shows the spread of bias scores across percentiles
                                    </Text>
                                    {renderDistributionChart()}
                                </CardBody>
                            </Card>
                        )}

                        {/* Metadata */}
                        <Card 
                            bg={placeholderBg} 
                            borderRadius="lg" 
                            boxShadow="sm" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" color={labelTextColor} mb={2}>
                                            Scope & Filters
                                        </Text>
                                        <VStack align="start" spacing={1}>
                                            <Text fontSize="xs" color={bodyTextColor}>
                                                Scope: <strong>{benchmarkData.scope}</strong>
                                            </Text>
                                            <Text fontSize="xs" color={bodyTextColor}>
                                                Time Window: <strong>{benchmarkData.time_window}</strong>
                                            </Text>
                                            {benchmarkData.filters?.segment && (
                                                <Text fontSize="xs" color={bodyTextColor}>
                                                    Segment: <strong>{benchmarkData.filters.segment}</strong>
                                                </Text>
                                            )}
                                            {benchmarkData.filters?.country && (
                                                <Text fontSize="xs" color={bodyTextColor}>
                                                    Country: <strong>{benchmarkData.filters.country}</strong>
                                                </Text>
                                            )}
                                        </VStack>
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm" fontWeight="medium" color={labelTextColor} mb={2}>
                                            Generated
                                        </Text>
                                        <Text fontSize="xs" color={bodyTextColor}>
                                            {new Date(benchmarkData.generated_at).toLocaleString()}
                                        </Text>
                                    </Box>
                                </SimpleGrid>
                            </CardBody>
                        </Card>
                    </>
                )}

                {/* No Data State */}
                {!loading && benchmarkData && (!benchmarkData.series || benchmarkData.series.length === 0) && (
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <VStack py={10} spacing={4}>
                                <Icon as={InfoIcon} boxSize={12} color={mutedTextColor} />
                                <Text fontSize="lg" color={mutedTextColor} fontWeight="medium">
                                    No benchmark data available
                                </Text>
                                <Text fontSize="sm" color={mutedTextColor} textAlign="center">
                                    Try adjusting your filters or time window to see results
                                </Text>
                            </VStack>
                        </CardBody>
                    </Card>
                )}
            </VStack>
        </Container>
    );
}

export default DashboardHeadBiasBenchmarkPaid;